/**
 * @description Company switcher dropdown for the sidebar
 *
 * Key features:
 * - Shows current company name + role badge in sidebar
 * - Dropdown with company list + "Add a Company" option (always accessible)
 * - 3-dot context menu per company with "Leave Company" option
 * - Confirmation modal before leaving (prevents accidental departure)
 * - Loading state during company switch with spinner
 * - Collapses to icon-only when sidebar is collapsed
 * - Click-outside and Escape key to close dropdown
 *
 * @dependencies
 * - AuthContext: company, companies[], switchCompany(), refreshContext()
 * - apiClient: POST /api/auth/leave-company
 * - lucide-react: Icons
 *
 * @notes
 * - Derives active company from AuthContext company, falls back to companies[0]
 * - Single-company users see company name with dropdown for "Add a Company"
 * - Company switch clears backend cache and re-fetches all context
 * - "Leave Company" sets employment_status = 'terminated' via backend
 * - Sole admin cannot leave (backend returns LEAVE_SOLE_ADMIN error)
 */

import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check, Loader2, Plus, MoreVertical, LogOut, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useCompany } from '../../contexts/AuthContext';
import apiClient from '../../services/api/apiClient';

const CompanySwitcher = ({ isSidebarOpen, isMobile = false }) => {
  const { companies, switchCompany, refreshContext } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [contextMenuId, setContextMenuId] = useState(null);
  const [leaveConfirm, setLeaveConfirm] = useState(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState(null);
  const dropdownRef = useRef(null);
  const contextMenuRef = useRef(null);

  // Derive active company: prefer AuthContext company, fall back to first in companies list
  const activeCompany = company || (companies.length > 0 ? companies[0] : null);
  const activeCompanyId = activeCompany?.id;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setContextMenuId(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Escape key to close dropdown or context menu
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (leaveConfirm) {
          setLeaveConfirm(null);
          setLeaveError(null);
        } else if (contextMenuId) {
          setContextMenuId(null);
        } else {
          setIsOpen(false);
        }
      }
    };

    if (isOpen || leaveConfirm) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, contextMenuId, leaveConfirm]);

  // Don't render until we have something to display
  if (!activeCompany && companies.length === 0) return null;

  const showLabel = isMobile || isSidebarOpen;
  const displayName = activeCompany?.businessName || 'Select company';

  const handleSwitch = async (companyId) => {
    if (companyId === activeCompanyId || isSwitching) return;

    setIsSwitching(true);
    try {
      await switchCompany(companyId);
    } finally {
      setIsSwitching(false);
      setIsOpen(false);
    }
  };

  const handleCreateCompany = () => {
    setIsOpen(false);
    navigate('/merchant/create-company');
  };

  const handleContextMenu = (e, companyId) => {
    e.stopPropagation();
    setContextMenuId(contextMenuId === companyId ? null : companyId);
  };

  const handleLeaveClick = (companyToLeave) => {
    setContextMenuId(null);
    setIsOpen(false);
    setLeaveError(null);
    setLeaveConfirm(companyToLeave);
  };

  const handleLeaveConfirm = async () => {
    if (!leaveConfirm || isLeaving) return;

    setIsLeaving(true);
    setLeaveError(null);

    try {
      await apiClient.post('/auth/leave-company', {
        companyId: leaveConfirm.id
      });

      const wasActive = leaveConfirm.id === activeCompanyId;
      setLeaveConfirm(null);

      // Refresh context to remove the company from the list
      await refreshContext();

      if (wasActive) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      if (errorCode === 'LEAVE_SOLE_ADMIN') {
        setLeaveError(errorMessage || 'You are the only admin. Please assign another admin before leaving.');
      } else {
        setLeaveError(errorMessage || 'Failed to leave company. Please try again.');
      }
    } finally {
      setIsLeaving(false);
    }
  };

  const handleLeaveCancel = () => {
    setLeaveConfirm(null);
    setLeaveError(null);
  };

  return (
    <>
      <div className="relative mb-3 pb-3 border-b border-gray-800" ref={dropdownRef}>
        <button
          onClick={() => !isSwitching && setIsOpen(!isOpen)}
          className="w-full flex items-center py-2 rounded-lg transition-colors duration-200 hover:bg-gray-800 cursor-pointer"
          disabled={isSwitching}
          aria-label="Company menu"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <div className="w-12 flex items-center justify-center flex-shrink-0">
            {isSwitching ? (
              <Loader2 className="w-5 h-5 text-brand animate-spin" />
            ) : (
              <Building2 className="w-5 h-5 text-brand" />
            )}
          </div>

          <div className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
            isMobile ? 'opacity-100 max-w-full' : showLabel ? 'opacity-100 max-w-full delay-[50ms]' : 'opacity-0 max-w-0'
          }`}>
            <div className="min-w-0 text-left">
              <div className="text-sm font-semibold text-white truncate pr-1">
                {displayName}
              </div>
            </div>

            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 mr-2 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`} />
          </div>
        </button>

        {/* Tooltip for collapsed sidebar */}
        {!isMobile && !isSidebarOpen && (
          <div className="absolute left-14 top-1/2 -translate-y-1/2 bg-gray-800 text-white px-2 py-1 rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
            {displayName}
          </div>
        )}

        {/* Dropdown menu */}
        {isOpen && (
          <div
            className={`absolute z-50 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg py-1 min-w-[220px] ${
              !isMobile && !isSidebarOpen ? 'left-14 top-0' : 'left-0 right-0'
            }`}
            role="listbox"
            aria-label="Select company"
          >
            {companies.map((c) => {
              const isActive = c.id === activeCompanyId;
              return (
                <div key={c.id} className="relative group/item">
                  <button
                    onClick={() => handleSwitch(c.id)}
                    disabled={isSwitching}
                    role="option"
                    aria-selected={isActive}
                    className={`w-full flex items-center px-3 py-2.5 text-left transition-colors ${
                      isActive
                        ? 'bg-gray-700/50 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate pr-6">{c.businessName}</div>
                      {c.roleName && (
                        <span className="inline-block mt-0.5 text-xs bg-gray-600 text-gray-200 rounded px-1.5 py-0.5">
                          {c.roleName}
                        </span>
                      )}
                    </div>
                    {isActive && (
                      <Check className="w-4 h-4 text-brand flex-shrink-0 ml-2" />
                    )}
                  </button>

                  {/* 3-dot context menu trigger — only on non-active companies */}
                  {!isActive && (
                    <button
                      onClick={(e) => handleContextMenu(e, c.id)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-white hover:bg-gray-600 opacity-0 group-hover/item:opacity-100 transition-opacity"
                      aria-label={`Options for ${c.businessName}`}
                    >
                      <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Context menu popover */}
                  {contextMenuId === c.id && (
                    <div
                      ref={contextMenuRef}
                      className="absolute right-0 top-full z-50 mt-1 bg-gray-900 border border-gray-600 rounded-lg shadow-lg py-1 min-w-[160px]"
                    >
                      <button
                        onClick={() => handleLeaveClick(c)}
                        className="w-full flex items-center px-3 py-2 text-left text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors text-sm"
                      >
                        <LogOut className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                        Leave Company
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Divider + Create New Company */}
            <div className={`${companies.length > 0 ? 'border-t border-gray-700 mt-1 pt-1' : ''}`}>
              <button
                onClick={handleCreateCompany}
                className="w-full flex items-center px-3 py-2 text-left text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
              >
                <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="text-sm">Add a Company</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Leave Company Confirmation Modal */}
      {leaveConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={!isLeaving ? handleLeaveCancel : undefined}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Leave {leaveConfirm.businessName}?
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  You will lose access to this company's dashboard, data, and tools.
                  A company admin can re-invite you if needed.
                </p>
              </div>
            </div>

            {/* Error message */}
            {leaveError && (
              <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{leaveError}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleLeaveCancel}
                disabled={isLeaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLeaveConfirm}
                disabled={isLeaving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isLeaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Leaving...
                  </>
                ) : (
                  'Leave Company'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanySwitcher;
